<template>
  <div class="profile-page">
    <div class="skip-links">
      <a href="#main-content" class="skip-link">Zum Hauptinhalt</a>
    </div>

    <div class="container-fluid px-4 py-5" id="main-content">
      <div class="row mb-4">
        <div class="col-md-8 offset-md-2">
          <h1><font-awesome-icon icon="user" /> Profil</h1>
        </div>
      </div>
      <div class="row">
        <div class="col-md-8 offset-md-2">
          <div class="card">
            <div class="card-body">
              <div>
                              <!-- Getting Started Tutorial -->
              <div class="mb-5">
                <label for="email" class="form-label">E-Mail</label>
                  <div class="input-group">
                    <input
                      type="email"
                      class="form-control"
                      id="email"
                      v-model="user.email"
                      :disabled="true"
                      required
                      tabindex="1"
                      v-if="user?.email"
                    />
                    <button
                      v-if="!user?.isEmailInChange"
                      type="button"
                      class="btn btn-secondary"
                      @click="showEmailChangeForm = true"
                      tabindex="6"
                    >
                      Ändern
                    </button>
                  </div>

                  <div v-if="user?.isEmailInChange" class="alert alert-gradient-warning mt-2">
                    <div class="d-flex flex-column">
                      <div>
                        <p class="mb-1">E-Mail-Änderung in Bearbeitung</p>
                        <p class="mb-0 small">
                          <i
                            >Neue E-Mail: <strong>{{ user.isEmailInChange }}</strong></i
                          >
                        </p>
                      </div>
                      <div class="d-flex flex-column flex-lg-row gap-2 mt-3">
                        <button
                          class="btn btn-danger"
                          @click="handleCancelEmailChange"
                          tabindex="7"
                        >
                          {{ cancelLoading ? 'Wird gesendet...' : 'Änderung abbrechen' }}
                        </button>
                        <button
                          class="btn btn-primary"
                          @click="handleResendEmailChange"
                          tabindex="8"
                        >
                          {{ resendLoading ? 'Wird gesendet...' : 'Bestätigung erneut senden' }}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="mb-5">
                  <label for="firstName" class="form-label">Vorname</label>
                  <input
                    type="text"
                    class="form-control"
                    id="firstName"
                    v-model="profile.firstName"
                    required
                    tabindex="2"
                  />
                  <label for="lastName" class="form-label">Nachname</label>
                  <input
                    type="text"
                    class="form-control"
                    id="lastName"
                    v-model="profile.lastName"
                    required
                    tabindex="3"
                  />
                </div>
                <div class="d-flex flex-column flex-lg-row gap-2">
                  <button
                    v-if="hasChanges"
                    type="button"
                    class="btn btn-primary text-white"
                    @click="handleUpdateProfile"
                    :disabled="loading"
                    tabindex="9"
                  >
                    <font-awesome-icon icon="user-edit" class="me-1" />
                    Profil aktualisieren
                  </button>
                  <button
                    type="button"
                    class="btn btn-warning"
                    @click="handleUpdatePassword"
                    :disabled="loading"
                    tabindex="10"
                  >
                    <font-awesome-icon icon="key" class="me-1" />
                    Passwort ändern
                  </button>
                  <button
                    type="button"
                    class="btn btn-primary"
                    @click="handlePrefillBillingData"
                    :disabled="loading"
                    tabindex="11"
                  >
                    <font-awesome-icon icon="file-invoice" class="me-1" />
                    {{ hasInvoiceData ? 'Rechnungsdaten bearbeiten' : 'Rechnungsdaten hinzufügen' }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Eigene Unterschrift -->
        <div class="col-md-8 offset-md-2 mt-4">
          <div class="card">
            <div class="card-body d-flex flex-column flex-lg-row align-items-lg-center gap-3">
              <div>
                <h5 class="mb-1">
                  <font-awesome-icon icon="signature" class="me-2" />
                  Eigene Unterschrift
                </h5>
                <p class="text-muted mb-2">
                  {{ currentSignatureExists ? 'Du hast bereits eine Unterschrift gespeichert.' : 'Speichere deine persönliche Unterschrift, um sie im Quittungsassistenten schnell einzufügen.' }}
                </p>
                <div
                  v-if="currentSignatureExists"
                  class="border rounded bg-white p-3 d-inline-block signature-preview"
                  aria-label="Gespeicherte Unterschrift Vorschau"
                >
                  <img
                    :src="profile.signature"
                    alt="Gespeicherte Unterschrift"
                    class="img-fluid signature-protected"
                    style="max-height: 120px"
                    draggable="false"
                    @dragstart.prevent
                    @contextmenu.prevent
                  />
                  <small
                    v-if="formattedSignatureUpdatedAt"
                    class="text-muted d-block mt-2 text-end"
                  >
                    Zuletzt aktualisiert: {{ formattedSignatureUpdatedAt }}
                  </small>
                </div>
              </div>
              <div class="ms-lg-auto">
                <button
                  type="button"
                  class="btn btn-outline-primary"
                  @click="openSignatureModal"
                  :disabled="loading"
                >
                  <font-awesome-icon icon="pen" class="me-1" />
                  Unterschrift bearbeiten
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Template Prefills -->
        <div class="col-md-8 offset-md-2 mt-4">
          <div class="card">
            <div class="card-header">
              <h5 class="mb-0">
                <font-awesome-icon icon="list-check" class="me-2" />
                Weitere Persönliche Daten
              </h5>
            </div>
            <div class="card-body">
              <div v-if="loadingTemplatePrefills" class="text-center py-3">
                <div class="spinner-border text-primary" role="status">
                  <span class="visually-hidden">Vorabfüllbare Felder werden geladen...</span>
                </div>
              </div>
              <div v-else>
                <p v-if="!hasTemplatePrefills" class="text-muted mb-0">
                  Für deine Templates sind aktuell keine vorabfüllbaren Felder verfügbar.
                </p>
                <div v-else class="d-flex flex-column gap-3">
                  <div
                    v-for="template in templatePrefills"
                    :key="template.id"
                    class="border rounded p-3 prefill-template-card"
                  >
                    <div class="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3">
                      <div>
                        <h6 class="mb-1">{{ template.displayName }}</h6>
                        <p v-if="template.description" class="text-muted small mb-2">
                          {{ template.description }}
                        </p>
                        <span class="badge bg-light text-dark">
                          {{ getPrefillFilledCount(template) }} / {{ getPrefillTotalCount(template) }} Felder vorbereitet
                        </span>
                        <div v-if="template.updatedAt" class="text-muted small mt-1">
                          Zuletzt aktualisiert:
                          {{ new Date(template.updatedAt).toLocaleString('de-DE') }}
                        </div>
                      </div>
                      <div class="ms-md-auto">
                        <button
                          type="button"
                          class="btn btn-primary btn-sm"
                          @click="openPrefillModal(template)"
                        >
                          <font-awesome-icon icon="pen" class="me-1" />
                          Vorlage bearbeiten
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Rechnungsdaten Anzeige -->
        <div v-if="hasInvoiceData" class="col-md-8 offset-md-2 mt-4">
          <div class="card">
            <div class="card-header">
              <h5 class="mb-0">
                <font-awesome-icon icon="file-invoice" class="me-2" />
                Gespeicherte Rechnungsdaten
              </h5>
            </div>
            <div class="card-body">
              <div class="invoice-data-display">
                <div v-if="invoiceData.company" class="mb-1">
                  {{ invoiceData.company }}
                </div>
                <div v-if="invoiceData.firstName || invoiceData.lastName" class="mb-1">
                  {{ invoiceData.firstName }} {{ invoiceData.lastName }}
                </div>
                <div v-if="invoiceData.street || invoiceData.number" class="mb-1">
                  {{ invoiceData.street }} {{ invoiceData.number }}
                </div>
                <div v-if="invoiceData.zip || invoiceData.city" class="mb-1">
                  {{ invoiceData.zip }} {{ invoiceData.city }}
                </div>
                <div v-if="invoiceData.country" class="mb-1">
                  {{ invoiceData.country }}
                </div>
                <div v-if="invoiceData.email" class="mb-1">
                  {{ invoiceData.email }}
                </div>
                <div v-if="invoiceData.taxId" class="mb-1">
                  Steuernummer: {{ invoiceData.taxId }}
                </div>
                <div v-if="invoiceData.vatId" class="mb-1">
                  USt-IdNr.: {{ invoiceData.vatId }}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-md-8 offset-md-2 mt-4">
          <div class="card">        
                <div class="card">
                  <div class="card-body">
                    <h5 class="card-title">
                      <font-awesome-icon icon="graduation-cap" class="me-2 text-info" />
                      Getting Started Tutorial
                    </h5>
                    <p class="card-text text-muted">
                      Lerne die wichtigsten Funktionen von Quitly kennen und erstelle deine erste Quittung.
                    </p>
                    <router-link to="/getting-started" class="btn btn-outline-info">
                      <font-awesome-icon :icon="user?.tutorial ? 'redo' : 'play'" class="me-2" />
                      {{ user?.tutorial ? 'Tutorial erneut starten' : 'Tutorial starten' }}
                    </router-link>
                  </div>
                </div>
              </div>
          </div>

        <!-- Benachrichtigungseinstellungen -->
        <div class="col-md-8 offset-md-2 mt-4">
          <div class="card">
            <div class="card-body">
              <div class="settings-section">
                <div class="section-header">
                  <h3><font-awesome-icon icon="bell" /> Benachrichtigungen</h3>
                  <p>Verwalte deine Benachrichtigungseinstellungen</p>
                </div>
                <div class="section-actions">
                  <router-link to="/notification-settings" class="btn btn-outline-primary">
                    <font-awesome-icon icon="cog" />
                    Benachrichtigungseinstellungen verwalten
                  </router-link>
                </div>
              </div>
            </div>
          </div>
        </div>
  
        <div class="col-md-8 offset-md-2 mt-4">
          <div 
            class="card quitly-danger-zone-card" 
            :class="{ 
              'quitly-danger-zone-collapsed': !isExpanded,
              'quitly-danger-zone-expanded': isExpanded 
            }"
          >
            <!-- Toggle Header with Chevron -->
            <div
              class="quitly-danger-zone-header d-flex justify-content-between align-items-center"
              role="button"
              @click="toggleDangerZone"
              :aria-expanded="isExpanded"
              aria-controls="dangerZone"
            >
              <h5 class="quitly-danger-zone-title mx-auto">Gefahrenbereich</h5>
              <font-awesome-icon
                :icon="['fas', 'chevron-down']"
                :class="['chevron-icon', { rotated: isExpanded }]"
                style="padding-right: 0.5rem;"
              />
            </div>
            <div 
              id="dangerZone" 
              :class="['collapse', { show: isExpanded }]"
            >
              <div class="card-body">
                <p class="text-danger fw-bold text-center mb-3">
                  Achtung: Diese Aktionen können nicht rückgängig gemacht werden!
                </p>
              </div>
              <div class="card-body pt-0">
                <div class="d-flex flex-column gap-2">
                  <button
                    type="button"
                    class="btn btn-dark w-100"
                    @click="handleKillSession"
                    :disabled="loading"
                    tabindex="12"
                  >
                    <font-awesome-icon icon="right-from-bracket" class="me-2" />
                    Überall ausloggen
                  </button>
                  <button
                    type="button"
                    class="btn btn-danger w-100"
                    @click="handleUserDelete"
                    :disabled="loading"
                    tabindex="13"
                  >
                    <font-awesome-icon icon="trash" class="me-2" />
                    Profil löschen
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- E-Mail ändern Modal -->
      <EmailChangeModal 
        :show="showEmailChangeForm" 
        @close="closeEmailChangeForm"
        @submit="handleEmailChange"
        ref="emailChangeModal"
      />

      <!-- Passwort ändern Modal -->
      <PasswordChangeModal 
        :show="showPasswordChangeForm" 
        @close="closePasswordChangeForm"
        @submit="handlePasswordSubmit"
        ref="passwordChangeModal"
      />

      <!-- Rechnungsdaten Modal -->
      <ProfileBillingForm 
        :show="showInvoiceDataForm" 
        @close="closeInvoiceDataForm"
        @saved="handleInvoiceDataSaved"
        ref="profileBillingForm"
      />

      <!-- Unterschrift Modal -->
      <Teleport to="#modalTarget">
        <div
          v-if="showSignatureModal"
          class="modal fade show d-block"
          tabindex="-1"
          style="background-color: rgba(0, 0, 0, 0.5); z-index: 1071"
        >
          <div class="modal-dialog modal-lg modal-dialog-centered">
            <div class="modal-content">
              <div class="modal-header">
                <h5 class="modal-title">
                  <font-awesome-icon icon="signature" class="me-2" />
                  Eigene Unterschrift
                </h5>
                <button type="button" class="btn-close" @click="closeSignatureModal"></button>
              </div>
              <div class="modal-body">
                <p class="text-muted">
                  Zeichne deine Unterschrift im Feld unten. Speichere anschließend, um sie in zukünftigen Quittungen zu verwenden.
                </p>
                <div class="signature-container">
                  <canvas
                    ref="profileSignatureCanvas"
                    class="signature-canvas border rounded bg-white w-100"
                    width="600"
                    height="300"
                    @mousedown="startProfileSignature"
                    @mousemove="drawProfileSignature"
                    @mouseup="stopProfileSignature"
                    @mouseleave="stopProfileSignature"
                    @touchstart.prevent="startProfileSignature"
                    @touchmove.prevent="drawProfileSignature"
                    @touchend.prevent="stopProfileSignature"
                    @touchcancel.prevent="stopProfileSignature"
                  ></canvas>
                </div>
              </div>
              <div class="modal-footer d-flex flex-column flex-md-row gap-2 justify-content-between">
                <div class="d-flex flex-wrap gap-2">
                  <button
                    type="button"
                    class="btn btn-outline-secondary"
                    @click="clearProfileSignature"
                    :disabled="loading"
                  >
                    <font-awesome-icon icon="eraser" class="me-1" />
                    Löschen
                  </button>
                  <button
                    v-if="originalSignatureExists"
                    type="button"
                    class="btn btn-outline-secondary"
                    @click="restoreOriginalSignature"
                    :disabled="loading || !hasSignatureChanges"
                  >
                    <font-awesome-icon icon="undo" class="me-1" />
                    Zur gespeicherten Unterschrift
                  </button>
                </div>
                <div class="d-flex flex-wrap gap-2 justify-content-end">
                  <button type="button" class="btn btn-link" @click="closeSignatureModal">
                    Abbrechen
                  </button>
                  <button
                    type="button"
                    class="btn btn-primary"
                    @click="handleSaveSignature"
                    :disabled="loading || !hasSignatureChanges"
                  >
                    <font-awesome-icon icon="save" class="me-1" />
                    Unterschrift speichern
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Teleport>

      <!-- Template Prefill Modal -->
      <Teleport to="#modalTarget">
        <div
          v-if="showPrefillModal"
          class="modal fade show d-block"
          tabindex="-1"
          style="background-color: rgba(0, 0, 0, 0.5); z-index: 1071"
        >
          <div class="modal-dialog modal-lg modal-dialog-centered">
            <div class="modal-content">
              <div class="modal-header">
                <h5 class="modal-title">
                  <font-awesome-icon icon="list-check" class="me-2" />
                  Vorlage bearbeiten
                  <span
                    v-if="selectedPrefillTemplate && selectedPrefillTemplate.displayName"
                    class="text-muted"
                  >
                    - {{ selectedPrefillTemplate.displayName }}
                  </span>
                </h5>
                <button type="button" class="btn-close" @click="closePrefillModal"></button>
              </div>
              <form @submit.prevent="submitPrefillValues">
                <div class="modal-body">
                  <div v-if="!selectedPrefillTemplate">
                    <p class="text-muted mb-0">Keine Template-Daten verfügbar.</p>
                  </div>
                  <div v-else>
                    <div
                      v-for="field in selectedPrefillTemplate.fields"
                      :key="field.id"
                      class="mb-3"
                    >
                      <label class="form-label d-flex justify-content-between align-items-center">
                        <span>{{ field.displayName }}</span>
                      </label>
                      <p v-if="field.description" class="text-muted small mb-2">
                        {{ field.description }}
                      </p>
                      <div v-if="isPrefillTypeSupported(field.type)">
                        <template v-if="field.type === 'textarea'">
                          <textarea
                            v-model="prefillFormValues[field.name]"
                            class="form-control"
                            rows="3"
                            :placeholder="field.placeholder || ''"
                          ></textarea>
                        </template>
                        <template v-else-if="field.type === 'checkbox'">
                          <div class="form-check">
                            <input
                              class="form-check-input"
                              type="checkbox"
                              :id="`prefill-${field.id}`"
                              v-model="prefillFormValues[field.name]"
                            />
                            <label class="form-check-label" :for="`prefill-${field.id}`">
                              Wert auf "Ja" setzen
                            </label>
                          </div>
                        </template>
                        <template v-else-if="field.type === 'select'">
                          <select
                            class="form-select"
                            v-model="prefillFormValues[field.name]"
                          >
                            <option value="">-- Keine Vorauswahl --</option>
                            <option
                              v-for="option in field.options?.options || []"
                              :key="`${field.id}-${option}`"
                              :value="option"
                            >
                              {{ option }}
                            </option>
                          </select>
                        </template>
                        <template v-else-if="field.type === 'number'">
                          <input
                            type="number"
                            class="form-control"
                            v-model="prefillFormValues[field.name]"
                            :placeholder="field.placeholder || ''"
                            step="any"
                          />
                        </template>
                        <template v-else-if="field.type === 'date'">
                          <input
                            type="date"
                            class="form-control"
                            v-model="prefillFormValues[field.name]"
                          />
                        </template>
                        <template v-else-if="field.type === 'money'">
                          <input
                            type="text"
                            class="form-control"
                            v-model="prefillFormValues[field.name]"
                            inputmode="decimal"
                            :placeholder="field.placeholder || ''"
                          />
                          <div v-if="field.options?.unit" class="form-text">
                            Einheit: {{ field.options.unit }}
                          </div>
                        </template>
                        <template v-else>
                          <input
                            type="text"
                            class="form-control"
                            v-model="prefillFormValues[field.name]"
                            :placeholder="field.placeholder || ''"
                          />
                        </template>
                        <div class="form-text text-muted small mt-1">
                          Leer lassen, um keinen Wert vorzugeben.
                        </div>
                      </div>
                      <div v-else class="alert alert-warning mb-0">
                        Vorbefüllung für diesen Feldtyp wird aktuell nicht unterstützt.
                      </div>
                    </div>
                  </div>
                </div>
                <div class="modal-footer d-flex flex-column flex-md-row gap-2">
                  <button
                    type="button"
                    class="btn btn-outline-secondary w-100 w-md-auto"
                    @click="clearPrefillForm"
                    :disabled="savingPrefill"
                  >
                    <font-awesome-icon icon="eraser" class="me-1" />
                    Alle Felder leeren
                  </button>
                  <div class="ms-md-auto d-flex flex-column flex-md-row gap-2 w-100 w-md-auto">
                    <button
                      type="button"
                      class="btn btn-secondary w-100 w-md-auto"
                      @click="closePrefillModal"
                      :disabled="savingPrefill"
                    >
                      Abbrechen
                    </button>
                    <button
                      type="submit"
                      class="btn btn-primary w-100 w-md-auto"
                      :disabled="savingPrefill"
                    >
                      <span
                        v-if="savingPrefill"
                        class="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Speichern
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </Teleport>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, onBeforeUnmount, nextTick } from 'vue'
import { onBeforeRouteLeave } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useAuthStore } from '@/stores/auth'
import {
  getProfile,
  updateProfile,
  requestEmailChange,
  updatePassword,
  getTemplatePrefills,
  updateTemplatePrefill,
} from '@/api/auth'
import { getInvoiceData } from '@/api/invoiceData'
import EmailChangeModal from '@/components/EmailChangeModal.vue'
import PasswordChangeModal from '@/components/PasswordChangeModal.vue'
import ProfileBillingForm from './Purchase/components/ProfileBillingForm.vue'
import { getThemeColors, showConfirmDialog, showConfirmDialogWithDeny, showError, showSuccess } from '@/services/notification'

const authStore = useAuthStore()
const { user } = storeToRefs(authStore)
const loading = ref(false)

const isExpanded = ref(false)
const cancelLoading = ref(false)
const resendLoading = ref(false)

// Für ungespeicherte Änderungen
const originalData = ref(null)
const hasUnsavedChanges = ref(false)

const toggleDangerZone = () => {
  isExpanded.value = !isExpanded.value
}

const profile = ref({
  firstName: '',
  lastName: '',
  signature: null,
})

const showSignatureModal = ref(false)
const profileSignatureCanvas = ref(null)
const isDrawingProfileSignature = ref(false)
const signatureUpdatedAt = ref(null)

const templatePrefills = ref([])
const loadingTemplatePrefills = ref(false)
const showPrefillModal = ref(false)
const selectedPrefillTemplate = ref(null)
const prefillFormValues = ref({})
const savingPrefill = ref(false)
const supportedPrefillTypes = new Set(['text', 'textarea', 'number', 'date', 'select', 'checkbox', 'money'])

const hasTemplatePrefills = computed(() => templatePrefills.value.length > 0)

const normalizeProfileValue = (value) => (value === null || value === undefined ? '' : value)

const hasNameChanges = computed(() => {
  if (!user.value || !originalData.value) return false

  const firstNameChanged =
    normalizeProfileValue(profile.value.firstName) !== normalizeProfileValue(originalData.value.firstName)
  const lastNameChanged =
    normalizeProfileValue(profile.value.lastName) !== normalizeProfileValue(originalData.value.lastName)

  return firstNameChanged || lastNameChanged
})

const hasSignatureChanges = computed(() => {
  if (!user.value || !originalData.value) return false

  return (
    normalizeProfileValue(profile.value.signature) !== normalizeProfileValue(originalData.value.signature)
  )
})

const hasChanges = computed(() => hasNameChanges.value || hasSignatureChanges.value)

const originalSignatureExists = computed(
  () => normalizeProfileValue(originalData.value?.signature) !== ''
)

const currentSignatureExists = computed(
  () => normalizeProfileValue(profile.value.signature) !== ''
)

const formattedSignatureUpdatedAt = computed(() => {
  if (!signatureUpdatedAt.value) {
    return ''
  }

  try {
    return new Intl.DateTimeFormat('de-DE', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(signatureUpdatedAt.value))
  } catch (error) {
    return ''
  }
})

const checkForChanges = () => {
  if (!originalData.value) return false

  return (
    normalizeProfileValue(profile.value.firstName) !== normalizeProfileValue(originalData.value.firstName) ||
    normalizeProfileValue(profile.value.lastName) !== normalizeProfileValue(originalData.value.lastName) ||
    normalizeProfileValue(profile.value.signature) !== normalizeProfileValue(originalData.value.signature)
  )
}

const hasInvoiceData = computed(() => {
  return invoiceData.value !== null && invoiceData.value !== undefined
})

const getPrefillFilledCount = (template) => {
  if (!template?.fields) return 0
  return template.fields.filter((field) => field.hasValue).length
}

const getPrefillTotalCount = (template) => {
  return template?.fields?.length || 0
}

const isPrefillTypeSupported = (type) => supportedPrefillTypes.has(type)

const initializePrefillForm = (template) => {
  const formValues = {}
  if (!template?.fields) {
    prefillFormValues.value = formValues
    return
  }

  template.fields.forEach((field) => {
    if (field.type === 'checkbox') {
      formValues[field.name] = field.value === true
    } else if (field.type === 'number') {
      formValues[field.name] =
        field.value !== null && field.value !== undefined ? String(field.value) : ''
    } else if (field.type === 'date') {
      formValues[field.name] = field.value || ''
    } else {
      formValues[field.name] =
        field.value !== null && field.value !== undefined ? String(field.value) : ''
    }
  })

  prefillFormValues.value = formValues
}

const clearPrefillForm = () => {
  if (!selectedPrefillTemplate.value?.fields) return

  const cleared = {}
  selectedPrefillTemplate.value.fields.forEach((field) => {
    if (field.type === 'checkbox') {
      cleared[field.name] = false
    } else {
      cleared[field.name] = ''
    }
  })

  prefillFormValues.value = cleared
}

const buildPrefillPayload = () => {
  if (!selectedPrefillTemplate.value?.fields) return {}

  const payload = {}
  selectedPrefillTemplate.value.fields.forEach((field) => {
    const rawValue = prefillFormValues.value[field.name]

    if (field.type === 'checkbox') {
      payload[field.name] = rawValue === true
      return
    }

    if (rawValue === null) {
      payload[field.name] = null
      return
    }

    if (typeof rawValue === 'string') {
      payload[field.name] = rawValue
      return
    }

    payload[field.name] = rawValue
  })

  return payload
}

const closePrefillModal = () => {
  showPrefillModal.value = false
  selectedPrefillTemplate.value = null
  prefillFormValues.value = {}
}

const openPrefillModal = (template) => {
  // recipientEmail sicherstellen, dass es vorhanden ist
  if (template && template.fields) {
    const hasRecipientEmail = template.fields.some((field) => field.name === 'recipientEmail')
    if (!hasRecipientEmail) {
      template.fields.push({
        id: `recipient-email-${template.id}`,
        name: 'recipientEmail',
        displayName: 'Empfänger-E-Mail',
        type: 'text',
        category: 'personal',
        required: false,
        placeholder: 'E-Mail-Adresse des Empfängers',
        description: null,
        defaultValue: null,
        options: null,
        allowPrefill: true,
        order: 9999,
        value: null,
        hasValue: false,
      })
    }
  }
  
  selectedPrefillTemplate.value = template
  initializePrefillForm(template)
  showPrefillModal.value = true
}

const loadTemplatePrefills = async () => {
  loadingTemplatePrefills.value = true
  try {
    const response = await getTemplatePrefills()
    const prefills = response.data || []
    
    // recipientEmail sicherstellen, dass es in allen Templates vorhanden ist
    templatePrefills.value = prefills.map((template) => {
      const hasRecipientEmail = template.fields?.some((field) => field.name === 'recipientEmail')
      // recipientEmail wird bereits vom Backend hinzugefügt, daher ist keine zusätzliche
      // Behandlung hier nötig. Das Backend stellt sicher, dass es immer vorhanden ist.
      return template
    })
  } catch (error) {
    console.error('Fehler beim Laden der Template-Vorabfüllungen:', error)
    showError('Vorabfüllbare Felder konnten nicht geladen werden.')
  } finally {
    loadingTemplatePrefills.value = false
  }
}

const submitPrefillValues = async () => {
  if (!selectedPrefillTemplate.value) {
    return
  }

  savingPrefill.value = true
  try {
    const payload = buildPrefillPayload()
    const response = await updateTemplatePrefill(selectedPrefillTemplate.value.id, payload)
    const saved = response.data || response
    const savedValues = saved?.values || {}

    const templateIndex = templatePrefills.value.findIndex(
      (item) => item.id === selectedPrefillTemplate.value.id,
    )

    if (templateIndex !== -1) {
      const updatedFields = templatePrefills.value[templateIndex].fields.map((field) => {
        const hasValue = Object.prototype.hasOwnProperty.call(savedValues, field.name)
        const storedValue = hasValue ? savedValues[field.name] : null
        return {
          ...field,
          value: hasValue
            ? field.type === 'checkbox'
              ? Boolean(storedValue)
              : storedValue
            : null,
          hasValue,
        }
      })

      // recipientEmail immer zur fieldList hinzufügen, falls noch nicht vorhanden
      const hasRecipientEmail = updatedFields.some((field) => field.name === 'recipientEmail')
      if (!hasRecipientEmail) {
        const recipientEmailHasValue = Object.prototype.hasOwnProperty.call(
          savedValues,
          'recipientEmail'
        )
        updatedFields.push({
          id: `recipient-email-${selectedPrefillTemplate.value.id}`,
          name: 'recipientEmail',
          displayName: 'Empfänger-E-Mail',
          type: 'text',
          category: 'personal',
          required: false,
          placeholder: 'E-Mail-Adresse des Empfängers',
          description: null,
          defaultValue: null,
          options: null,
          allowPrefill: true,
          order: 9999,
          value: recipientEmailHasValue ? savedValues.recipientEmail : null,
          hasValue: recipientEmailHasValue,
        })
      }

      const updatedTemplate = {
        ...templatePrefills.value[templateIndex],
        fields: updatedFields,
        hasPrefillValues: Object.keys(savedValues).length > 0,
        updatedAt: saved.updatedAt || null,
      }
      templatePrefills.value.splice(templateIndex, 1, updatedTemplate)
      selectedPrefillTemplate.value = updatedTemplate
    }

    showSuccess('Vorabfüllung gespeichert')
    closePrefillModal()
  } catch (error) {
    console.error('Fehler beim Speichern der Vorabfüllung:', error)
    showError(error?.response?.data?.error || 'Vorabfüllung konnte nicht gespeichert werden')
  } finally {
    savingPrefill.value = false
  }
}
function initializeProfileSignatureCanvas() {
  const canvas = profileSignatureCanvas.value
  if (!canvas) return

  const context = canvas.getContext('2d')
  if (!context) return

  canvas.width = 600
  canvas.height = 300

  context.clearRect(0, 0, canvas.width, canvas.height)
  context.lineCap = 'round'
  context.lineJoin = 'round'
  context.strokeStyle = '#000'
  context.lineWidth = 2
}

function clearProfileSignatureCanvas() {
  const canvas = profileSignatureCanvas.value
  if (!canvas) return

  const context = canvas.getContext('2d')
  if (!context) return

  context.clearRect(0, 0, canvas.width, canvas.height)
}

function renderProfileSignature() {
  const canvas = profileSignatureCanvas.value
  if (!canvas) return

  initializeProfileSignatureCanvas()

  const signature = profile.value.signature
  if (!signature || typeof signature !== 'string') {
    return
  }

  const context = canvas.getContext('2d')
  if (!context) return

  const image = new Image()
  image.onload = () => {
    context.clearRect(0, 0, canvas.width, canvas.height)
    context.drawImage(image, 0, 0, canvas.width, canvas.height)
  }
  image.src = signature
}

function getProfileSignatureCoordinates(canvas, event) {
  const rect = canvas.getBoundingClientRect()
  const scaleX = canvas.width / rect.width
  const scaleY = canvas.height / rect.height

  let clientX
  let clientY

  if (event.type.includes('touch')) {
    const touch = event.touches?.[0] || event.changedTouches?.[0]
    if (!touch) {
      return { x: 0, y: 0 }
    }
    clientX = touch.clientX
    clientY = touch.clientY
  } else {
    clientX = event.clientX
    clientY = event.clientY
  }

  return {
    x: (clientX - rect.left) * scaleX,
    y: (clientY - rect.top) * scaleY,
  }
}

function startProfileSignature(event) {
  const canvas = profileSignatureCanvas.value
  if (!canvas) return

  event.preventDefault?.()
  isDrawingProfileSignature.value = true

  const context = canvas.getContext('2d')
  if (!context) return

  const coords = getProfileSignatureCoordinates(canvas, event)
  context.beginPath()
  context.moveTo(coords.x, coords.y)
}

function drawProfileSignature(event) {
  if (!isDrawingProfileSignature.value) return

  const canvas = profileSignatureCanvas.value
  if (!canvas) return

  event.preventDefault?.()
  const context = canvas.getContext('2d')
  if (!context) return

  const coords = getProfileSignatureCoordinates(canvas, event)

  context.lineWidth = 2
  context.lineCap = 'round'
  context.strokeStyle = '#000'
  context.lineTo(coords.x, coords.y)
  context.stroke()

  saveProfileSignatureFromCanvas()
}

function stopProfileSignature(event) {
  if (event?.preventDefault) {
    event.preventDefault()
  }
  if (!isDrawingProfileSignature.value) {
    return
  }
  isDrawingProfileSignature.value = false
}

function saveProfileSignatureFromCanvas() {
  const canvas = profileSignatureCanvas.value
  if (!canvas) return

  try {
    const dataUrl = canvas.toDataURL()
    profile.value.signature = dataUrl
  } catch (error) {
    console.error('Fehler beim Speichern der Signatur:', error)
  }
}

function clearProfileSignature() {
  profile.value.signature = null
  clearProfileSignatureCanvas()
}

function restoreOriginalSignature() {
  profile.value.signature = originalData.value?.signature || null
  renderProfileSignature()
}

function openSignatureModal() {
  showSignatureModal.value = true
}

function closeSignatureModal() {
  isDrawingProfileSignature.value = false
  showSignatureModal.value = false
  profileSignatureCanvas.value = null
}

const confirmBeforeLeaving = async () => {
  if (!hasUnsavedChanges.value) {
    return true
  }
  
  const result = await showConfirmDialogWithDeny(
    'Ungespeicherte Änderungen',
    'Du hast ungespeicherte Änderungen. Möchtest du diese speichern, bevor du fortfährst?',
    'Speichern',
    'Verwerfen',
    'Abbrechen',
    {
      icon: 'warning',
      confirmButtonColor: '#0d6efd',
      denyButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d'
    }
  )
  
  if (result.isConfirmed) {
    // Speichere die Änderungen vor dem Verlassen
    await saveProfile('Änderungen gespeichert')
    return true
  } else if (result.isDenied) {
    // Verwerfe die Änderungen
    hasUnsavedChanges.value = false
    return true
  } else {
    // Benutzer hat abgebrochen
    return false
  }
}


const showEmailChangeForm = ref(false)
const emailChangeModal = ref(null)
const showPasswordChangeForm = ref(false)
const passwordChangeModal = ref(null)
const showInvoiceDataForm = ref(false)
const profileBillingForm = ref(null)
const invoiceData = ref(null)

const handleKillSession = async () => {
  const result = await showConfirmDialog(
    'Möchtest du fortfahren?',
    'Durch diese Aktion wirst du auf allen Geräten (inklusive diesem) ausgeloggt.',
    'Ja, fortfahren',
    'Abbrechen',
    {icon: 'question', iconColor: getThemeColors().danger}
  )
  if(result.isConfirmed){
    await authStore.logout(true)
  }
}

const handleUserDelete = async () => {
    const result = await showConfirmDialog(
    'Möchtest du fortfahren?',
    'Diese Aktion kann nicht rückgängig gemacht werden! Du verlierst den Zugriff auf alle Quittungen und Teams.',
    'Ja, wirklich fortfahren',
    'Abbrechen',
    {icon: 'question', iconColor: getThemeColors().danger}
  )
  if(result.isConfirmed){
    await authStore.deleteMe()
  }
}


watch(
  user,
  async (newUser) => {
    if (newUser) {
      profile.value.firstName = newUser.firstName || ''
      profile.value.lastName = newUser.lastName || ''
      profile.value.signature = newUser.signature || null

      signatureUpdatedAt.value = newUser.signatureUpdatedAt || null

      originalData.value = {
        firstName: newUser.firstName || '',
        lastName: newUser.lastName || '',
        signature: newUser.signature || null,
      }

      await nextTick()
      initializeProfileSignatureCanvas()
      renderProfileSignature()
    }
  },
  { immediate: true },
)

// Watcher für Änderungen
watch(
  [() => profile.value.firstName, () => profile.value.lastName, () => profile.value.signature],
  () => {
    hasUnsavedChanges.value = checkForChanges()
  },
  { deep: true },
)

watch(
  showSignatureModal,
  async (isOpen) => {
    if (isOpen) {
      await nextTick()
      initializeProfileSignatureCanvas()
      renderProfileSignature()
    } else {
      isDrawingProfileSignature.value = false
      profileSignatureCanvas.value = null
    }
  },
)

const handleUpdatePassword = async () => {
  showPasswordChangeForm.value = true
}

const handlePasswordSubmit = async (newPassword) => {
  try {
    await updatePassword(newPassword)
    

    setTimeout(async () => {

      await authStore.logout(false)
    }, 3000)
  } catch (error) {
    console.error('Fehler beim Passwort ändern:', error)
    passwordChangeModal.value?.setError(
      error?.response?.data?.message || error.message || 'Fehler beim Passwort ändern'
    )
    throw error
  }
}

const handleEmailChange = async (newEmail) => {
  try {
    await requestEmailChange(newEmail)
    await authStore.updateProfile()
  } catch (error) {
    console.error('Fehler bei der E-Mail-Änderung:', error)
    emailChangeModal.value?.setError(
      error?.response?.data?.message || error.message || 'Fehler bei der E-Mail-Änderung'
    )
    throw error
  }
}

const saveProfile = async (successMessage = null) => {
  try {
    loading.value = true
    const payload = {
      firstName: profile.value.firstName,
      lastName: profile.value.lastName,
      signature: profile.value.signature || null,
    }
    await updateProfile(payload)
    const updatedUser = await authStore.updateProfile()
    if (updatedUser) {
      signatureUpdatedAt.value = updatedUser.signatureUpdatedAt || null
    }
    
    // Aktualisiere die ursprünglichen Daten nach erfolgreichem Speichern
    originalData.value = {
      firstName: profile.value.firstName,
      lastName: profile.value.lastName,
      signature: profile.value.signature || null,
    }
    hasUnsavedChanges.value = false
    
    if (successMessage) {
      showSuccess(successMessage)
    }
  } catch (error) {
    console.error('Fehler beim Aktualisieren des Profils:', error)
    showError(
      error?.response?.data?.message || error.message || 'Fehler beim Aktualisieren des Profils',
    )
    throw error
  } finally {
    loading.value = false
  }
}

const handleUpdateProfile = async () => {
  await saveProfile('Profil erfolgreich aktualisiert')
}

const handleSaveSignature = async () => {
  await saveProfile('Unterschrift gespeichert')
  showSignatureModal.value = false
}

const handleCancelEmailChange = async () => {
  try {
    cancelLoading.value = true
    await authStore.cancelEmailChange()
    await authStore.getProfile()
    showSuccess('E-Mail-Änderung wurde abgebrochen')
  } catch (error) {
    console.error('Fehler beim Abbrechen der E-Mail-Änderung:', error)
    showError(
      error?.response?.data?.message ||
        error.message ||
        'Fehler beim Abbrechen der E-Mail-Änderung',
    )
  } finally {
    cancelLoading.value = false
  }
}

const handleResendEmailChange = async () => {
  try {
    resendLoading.value = true
    await authStore.resendEmailChange()
    await getProfile()
    showSuccess('E-Mail-Änderung wurde erneut gesendet')
  } catch (error) {
    if (error.response?.status === 429) {
      showError('Zu viele Anfragen. Bitte warte einen Moment.')
    } else {
      showError(
        error?.response?.data?.message ||
          error.message ||
          'Fehler beim erneuten Senden der E-Mail-Änderung',
      )
    }
  } finally {
    resendLoading.value = false
  }
}

const closeEmailChangeForm = () => {
  showEmailChangeForm.value = false
}

const closePasswordChangeForm = () => {
  showPasswordChangeForm.value = false
}

const closeInvoiceDataForm = () => {
  showInvoiceDataForm.value = false
}

const handleInvoiceDataSaved = (savedInvoiceData) => {
  invoiceData.value = savedInvoiceData
  if (savedInvoiceData) {
    showSuccess('Rechnungsdaten erfolgreich gespeichert')
  } else {
    showSuccess('Rechnungsdaten erfolgreich gelöscht')
  }
}

const loadInvoiceData = async () => {
  try {
    const response = await getInvoiceData()
    if (response.success && response.data.invoiceData) {
      invoiceData.value = response.data.invoiceData
    } else {
      invoiceData.value = null
    }
  } catch (error) {
    console.error('Fehler beim Laden der Rechnungsdaten:', error)
    invoiceData.value = null
  }
}

const handlePrefillBillingData = () => {
  showInvoiceDataForm.value = true
}

// Router-Guard für Navigation
onBeforeRouteLeave(async (to, from, next) => {
  if (!hasUnsavedChanges.value) {
    next()
    return
  }
  
  const result = await showConfirmDialogWithDeny(
    'Ungespeicherte Änderungen',
    'Du hast ungespeicherte Änderungen. Möchtest du diese speichern, bevor du fortfährst?',
    'Speichern',
    'Verwerfen',
    'Abbrechen',
    {
      icon: 'warning',
      confirmButtonColor: '#0d6efd',
      denyButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d'
    }
  )
  
  if (result.isConfirmed) {
    // Speichere die Änderungen vor dem Verlassen
    try {
      await saveProfile()
      next()
    } catch (error) {
      console.error('Fehler beim Speichern:', error)
      next(false) // Verhindere Navigation bei Fehler
    }
  } else if (result.isDenied) {
    // Verwerfe die Änderungen und erlaube Navigation
    hasUnsavedChanges.value = false
    next()
  } else {
    // Benutzer hat abgebrochen - verhindere Navigation
    next(false)
  }
})

// Browser beforeunload Handler
const handleBeforeUnload = (e) => {
  if (hasUnsavedChanges.value) {
    e.preventDefault()
    e.returnValue = ''
  }
}

onMounted(async () => {
  await loadTemplatePrefills()
  await loadInvoiceData()
  await nextTick()
  initializeProfileSignatureCanvas()
  renderProfileSignature()
  window.addEventListener('beforeunload', handleBeforeUnload)
})

onBeforeUnmount(() => {
  window.removeEventListener('beforeunload', handleBeforeUnload)
})


</script>

<style scoped>
.settings-section {
  text-align: center;
  padding: 2rem;
}

.section-header h3 {
  color: #1e293b;
  margin-bottom: 0.5rem;
  font-size: 1.5rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
}

.section-header p {
  color: #64748b;
  margin-bottom: 1.5rem;
  font-size: 1rem;
}

.section-actions {
  margin-top: 1rem;
}

.section-actions .btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  font-weight: 500;
  text-decoration: none;
  transition: all 0.2s;
}

.section-actions .btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.prefill-template-card {
  transition: box-shadow 0.2s ease;
}

.prefill-template-card:hover {
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.05);
}

.signature-preview {
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}

.signature-protected {
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  -webkit-user-drag: none;
  -khtml-user-drag: none;
  -moz-user-drag: none;
  -o-user-drag: none;
}
</style>
